import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';

import { getCategoryAttributesForMparticle } from '@reverbdotcom/commons/src/csp_category_helper';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { ListingOfferMetadata } from '@reverbdotcom/commons/src/components/listing_offer_metadata';
import { WatchBadgeFragment } from '@reverbdotcom/commons/src/components/watch_badge';
import {
  FeaturedListingWithPrice,
  FeaturedListingCSP,
  CspFeaturedListing,
} from '../../../schemas/rql';

import FeaturedListingModule, { FeaturedListingModuleFragment } from '../../featured_listing_module';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { isDomesticToBuyer, Item2ProductViewTrackingFragment } from '../../use_product_view_action';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

export const CSPFeaturedListingFragment = {
  listing: gql`
    fragment FeaturedListingWithPrice on Listing {
      _id
      price {
        amount
        amountCents
      }
      bumped
      ...FeaturedListingModuleData
    }
    ${FeaturedListingModuleFragment}
  `,
  csp: gql`
    fragment FeaturedListingCSP on CSP {
      _id
      id
      inventory {
        newTotal
        usedTotal
      }
      categories {
        _id
        rootSlug
        slug
      }
    }
  `,
};

interface IProps {
  listing: FeaturedListingWithPrice.Fragment;
  csp: FeaturedListingCSP.Fragment;
  isBumped: boolean;
  isLoading: boolean;
  loggedOut?: boolean;
  queryForAdditionalFields?: boolean;
  hideMetadataAsOffer?: boolean;
  showBorder?: boolean;
}

const COMPONENT_NAME = 'CSPFeaturedListing';

function featuredListing(props: ChildProps<IProps, CspFeaturedListing.Query>) {
  if (props.queryForAdditionalFields && props?.data?.listing) {
    return {
      ...props.listing,
      ...props.data.listing,
    };
  }

  return props.listing;
}

function ListingMetadata(props: { listing: FeaturedListingWithPrice.Fragment, hide: boolean }) {
  if (props.hide) return null;

  return (
    <ListingOfferMetadata listing={props.listing} />
  );
}

export function CSPFeaturedListing(props: ChildProps<IProps, CspFeaturedListing.Query>) {
  const { csp, isBumped, isLoading } = props;
  const listing = featuredListing(props);
  const listingLoaded = props.queryForAdditionalFields ? !!props.data?.listing : !!listing;
  const loaded = !!csp && listingLoaded && !isLoading;
  const user = useUser();

  const cspData = !loaded ? {} : {
    cspId: csp.id,
    newInventory: csp.inventory.newTotal,
    usedInventory: csp.inventory.usedTotal,
    ...getCategoryAttributesForMparticle(csp),
  };

  const listingData = !loaded ? {} : {
    listing,
    bumped: isBumped,
    domesticToBuyer: isDomesticToBuyer(user.shippingRegionCode, listing.shop?.address?.countryCode),
    shipsToBuyer: Boolean(listing.shipping?.shippingPrices[0]),
  };

  const listingImpressionData = !loaded ? {} : {
    listingIds: [listing.id],
    listings: [listing],
  };

  const mParticleViewData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ViewedFeaturedListing,
    ...listingData,
    ...cspData,
  };

  useViewTracking(mParticleViewData, loaded);

  const impressionData = {
    eventName: MParticleEventName.ListingImpression,
    componentName: COMPONENT_NAME,
    shownAsBumped: isBumped,
    ...listingImpressionData,
  };

  useViewTracking(impressionData, loaded);

  return (
    <>
      {loaded && <ListingMetadata
        listing={listing}
        hide={props.hideMetadataAsOffer}
      />}
      <div className="site-wrapper">
        <FeaturedListingModule
          listing={!loaded ? null : listing}
          isBumped={isBumped}
          isLoading={!loaded || isLoading}
          trackName={COMPONENT_NAME}
          showBorder={props.showBorder}
        />
      </div>
    </>
  );
}

const connect = withGraphql<IProps, CspFeaturedListing.Query>(
  gql`
    query Csp_Featured_Listing(
      $id: String
    ){
      listing(
        input: {
          id: $id
        }
      ) {
        _id
        id
        ...FeaturedListingWithPrice
        ...WatchBadgeData
        ...Item2ProductViewTrackingData
      }
    }
    ${CSPFeaturedListingFragment.listing}
    ${WatchBadgeFragment}
    ${Item2ProductViewTrackingFragment}
  `,
  {
    skip: (ownProps) => !ownProps.queryForAdditionalFields || !ownProps.listing?.id,
    options: (ownProps) => {
      return {
        errorPolicy: 'all',
        ssr: false,
        variables: {
          id: ownProps.listing.id,
        },
      };
    },
  },
);

export default connect(CSPFeaturedListing);
